import React, { useCallback, useState } from 'react';

import type { UUID } from '@aline/common';
import { routes } from '@aline/routes';
import { alpha, Badge, IconButton } from '@mui/material';
import clsx from 'clsx';
import { Circle, CircleCheck, MessageSquare, Trash2 } from 'lucide-react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { AlineAiAvatar, AlineAvatar, AlineAvatarGroup, AlineTooltip } from 'src/components';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { IconColors } from 'src/components/icons/icon-utils';
import { Apollo } from 'src/gql/generated';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { getPermissionAssigneeDisplayDetails, getPermissionUser } from 'src/helpers';
import { useChangeTaskStatus, useDeleteTask } from 'src/hooks/tasks';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { makeStyles } from 'tss-react/mui';

import { getHasReviewers } from '../side-panel/task-review/task-review-helpers';
import { TaskReviewStatusPill } from '../side-panel/task-review/TaskStatusPill';
import { ReopenTaskDialog, useReopenTask } from '../side-panel/task-review/use-reopen-task';
import {
  ParkedWorkflowCompletionDialog,
  useParkedWorkflowCompletion,
} from '../use-parked-workflow-completion';
import type { ParkedWorkflowMeta } from '../use-parked-workflow-completion';
import { useWorkflowDetailLinks } from '../WorkflowDetailContext';

const useStyles = makeStyles<
  {
    isSelected: boolean;
    isSubtask: boolean;
  },
  'visibleOnHover'
>()((theme, { isSelected, isSubtask }, classes) => ({
  actionButton: {
    '& svg': {
      color: 'inherit',
    },
    color: theme.appColors.iconsType ?? 'rgba(55, 73, 81, 0.6)',
    padding: theme.spacing(0.5),
  },
  avatar: {
    flexShrink: 0,
  },
  completeButton: {
    display: 'inline-flex',
    flexShrink: 0,
  },
  completeIcon: {
    '& circle': {
      stroke: IconColors.DarkGrey,
    },
    '& path': {
      stroke: IconColors.White,
    },
  },
  root: {
    // Translucent blues, so they show on the white phase card and on the gray phaseless page.
    '&:hover': {
      backgroundColor: isSelected
        ? alpha(theme.appColors.primary, 0.12)
        : theme.appColors.hoverBackgroundSubtle,
    },
    [`&:hover .${classes.visibleOnHover}`]: {
      opacity: 1,
    },
    alignItems: 'center',
    backgroundColor: isSelected ? alpha(theme.appColors.primary, 0.12) : undefined,
    borderRadius: 6,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(0.75),
    minHeight: 32,
    padding: theme.spacing(0.5, 2, 0.5, isSubtask ? 5 : 2),
  },
  taskPanelButton: {
    '& svg': {
      color: 'inherit',
    },
    '&:hover': {
      borderColor: theme.appColors.primary ?? theme.palette.primary.main,
      color: theme.appColors.primary ?? theme.palette.primary.main,
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: '1px solid #d9d9d9',
    borderRadius: 8,
    color: theme.appColors.iconsType ?? 'rgba(55, 73, 81, 0.6)',
    display: 'inline-flex',
    justifyContent: 'center',
    padding: theme.spacing(0.375, 0.75),
  },
  title: {
    flex: 1,
    fontSize: 14,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  titleCompleted: {
    color: 'rgba(55, 73, 81, 0.4)',
    textDecoration: 'line-through',
  },
  titleInput: {
    '&:focus': {
      borderBottom: `1px solid ${theme.appColors.primary}`,
    },
    background: 'transparent',
    border: 'none',
    flex: 1,
    fontSize: 14,
    minWidth: 0,
    outline: 'none',
    padding: 0,
  },
  visibleOnHover: {
    opacity: 0,
    transition: 'opacity 150ms',
  },
}));

type TaskSubSet = ApolloTypes.PhaseSimple_NonSigningPhase_Fragment['tasks'][number];
type PhaseSubSet = ApolloTypes.PhaseSimple_NonSigningPhase_Fragment;

const CIRCLE_ACTION = {
  Complete: 'complete',
  OpenPanel: 'openPanel',
  Reopen: 'reopen',
} as const;
type CircleAction = (typeof CIRCLE_ACTION)[keyof typeof CIRCLE_ACTION];

interface PhaseTaskRowProps {
  phase: PhaseSubSet;
  task: TaskSubSet;
  workflow: ParkedWorkflowMeta;
  /** Set when the row sits under a status heading that already says it. */
  hideStatus?: boolean;
  isSubtask?: boolean;
  readOnly?: boolean;
}

export const PhaseTaskRow = React.memo(function PhaseTaskRow({
  hideStatus = false,
  isSubtask = false,
  phase,
  readOnly = false,
  task,
  workflow,
}: PhaseTaskRowProps) {
  const reopen = useReopenTask(task.id);
  const commentCount = task.comments.length;
  const isCompleted = Boolean(task.completedAt);
  const { handleDelete, handleRowClick, isSelected } = usePhaseTaskRow(task, phase, workflow.id);
  const { classes } = useStyles({ isSelected, isSubtask });
  const isTemplateTask = task.variant === Apollo.TaskVariant.Template;
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const stopRowClickPropagation = (e: React.MouseEvent) => e.stopPropagation();
  const editDisabledReason = task.requesterDisallowedReason.edit;
  const changeStatusDisabledReason = task.requesterDisallowedReason.changeStatus;
  const { loading: changingStatus, markComplete } = useChangeTaskStatus(task.id);
  const { completeTask, confirmAndComplete, dismissPrompt, prompt } = useParkedWorkflowCompletion({
    onComplete: markComplete,
    workflow,
  });
  const circle = getCompletionCircle({
    hasReviewers: getHasReviewers(task.assignments),
    isCompleted,
  });

  const handleCircleClick = (e: React.MouseEvent) => {
    stopRowClickPropagation(e);
    switch (circle.action) {
      case CIRCLE_ACTION.Reopen:
        return reopen.requestReopen();
      case CIRCLE_ACTION.OpenPanel:
        return handleRowClick(e);
      case CIRCLE_ACTION.Complete:
        return completeTask();
    }
  };

  return (
    <>
      <div className={classes.root} role="button" tabIndex={0} onClick={handleRowClick}>
        <AlineTooltip arrow placement="top" title={changeStatusDisabledReason || circle.label}>
          <span className={classes.completeButton}>
            <IconButton
              aria-label={circle.label}
              disabled={readOnly || Boolean(changeStatusDisabledReason) || changingStatus}
              size="small"
              onClick={handleCircleClick}
            >
              <CompletionIcon className={classes.completeIcon} isCompleted={isCompleted} />
            </IconButton>
          </span>
        </AlineTooltip>
        <div className={clsx(classes.title, isCompleted && classes.titleCompleted)}>
          {task.title}
        </div>
        <AlineTooltip arrow placement="top" title={editDisabledReason || 'Delete task'}>
          <span>
            <IconButton
              aria-label="Delete task"
              className={classes.visibleOnHover}
              disabled={readOnly || Boolean(editDisabledReason)}
              size="small"
              onClick={e => {
                stopRowClickPropagation(e);
                setConfirmDeleteOpen(true);
              }}
            >
              <Trash2 size={16} />
            </IconButton>
          </span>
        </AlineTooltip>
        {!isTemplateTask && commentCount > 0 && (
          <AlineTooltip
            arrow
            placement="top"
            title={`${commentCount} comment${commentCount > 1 ? 's' : ''} in activity`}
          >
            <IconButton aria-label="Comments" size="small" onClick={handleRowClick}>
              <Badge
                anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                badgeContent={commentCount}
                color="primary"
                overlap="rectangular"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: 8,
                    height: 14,
                    minWidth: 14,
                    padding: '0 2px',
                    right: 0,
                    top: 0,
                  },
                }}
              >
                <MessageSquare size={16} />
              </Badge>
            </IconButton>
          </AlineTooltip>
        )}
        {!hideStatus && <TaskReviewStatusPill status={task.status} />}

        <div className={classes.avatar}>
          <AlineAvatarGroup>
            {task.assignments.map(({ permission }) => {
              const displayDetails = getPermissionAssigneeDisplayDetails(permission);
              return (
                <AlineTooltip
                  key={permission.id}
                  arrow
                  placement="top"
                  title={displayDetails.primary}
                >
                  <AlineAvatar
                    avatarInfo={getPermissionUser(permission) ?? permission.role}
                    styles={{ backgroundColor: displayDetails.avatarColor, size: 'sm' }}
                  />
                </AlineTooltip>
              );
            })}
            {task.isDelegatedToAi && (
              <AlineTooltip arrow placement="top" title="Aline AI">
                <AlineAiAvatar />
              </AlineTooltip>
            )}
          </AlineAvatarGroup>
        </div>
      </div>
      <ConfirmationDialog
        message={`Delete task "${task.title || 'Untitled'}"? This action cannot be undone.`}
        open={confirmDeleteOpen}
        title="Delete Task"
        onClick={stopRowClickPropagation}
        onClickPrimary={handleDelete}
        onClose={() => setConfirmDeleteOpen(false)}
      />
      <ParkedWorkflowCompletionDialog
        prompt={prompt}
        onClose={dismissPrompt}
        onConfirm={confirmAndComplete}
      />
      <ReopenTaskDialog
        nextRound={reopen.nextRound}
        open={reopen.isPromptOpen}
        onClose={reopen.dismissPrompt}
        onConfirm={reopen.confirmReopen}
      />
    </>
  );
});

/**
 * The circle runs the row's primary action. A task with reviewers finishes by
 * submitting with a note, which happens in the panel, so the circle opens it.
 */
function getCompletionCircle(options: { hasReviewers: boolean; isCompleted: boolean }): {
  action: CircleAction;
  label: string;
} {
  if (options.isCompleted) return { action: CIRCLE_ACTION.Reopen, label: 'Reopen task' };
  if (options.hasReviewers)
    return { action: CIRCLE_ACTION.OpenPanel, label: 'Open to submit for review' };
  return { action: CIRCLE_ACTION.Complete, label: 'Mark complete' };
}

function CompletionIcon({ className, isCompleted }: { className: string; isCompleted: boolean }) {
  if (isCompleted) return <CircleCheck className={className} fill="#00A82D" size={20} />;
  return <Circle size={20} />;
}

function usePhaseTaskRow(task: TaskSubSet, phase: PhaseSubSet, workflowId: UUID) {
  const navigate = useNavigate();
  const location = useLocation();
  const { deleteTask } = useDeleteTask();
  const { openDrawer } = useGlobalDrawer();
  const links = useWorkflowDetailLinks(workflowId);

  const taskMatch = matchPath(
    { end: false, path: routes.WorkflowDetailTabTask },
    location.pathname
  );
  const isSelected = taskMatch?.params.taskId === task.id;

  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      openDrawer();
      navigate(links.task(task.id));
    },
    [links, navigate, openDrawer, task.id]
  );

  const handleDelete = useCallback(async () => {
    const { error } = await deleteTask(task.id, workflowId, phase.id);
    if (!error) {
      if (isSelected) navigate(links.baseUrl(), { replace: true });
    }
  }, [deleteTask, isSelected, links, navigate, phase.id, task.id, workflowId]);

  return {
    handleDelete,
    handleRowClick,
    isSelected,
  };
}
